import {
  APPROVAL_MODE_LABELS,
  ApprovalMode,
  TICKET_PRIORITY_LABELS,
  TICKET_TYPE_LABELS,
  TicketPriority,
  TicketType,
  centsToEuroInput,
  eurosToCents,
  type ApprovalStep,
  type ApprovalWorkflow,
  type UpsertApprovalStepRequest,
  type UpsertApprovalWorkflowRequest,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { useVoiceCopy } from '@/platform/voice/use-voice';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useApprovalLookups,
  useApprovalWorkflows,
  useCreateApprovalStep,
  useCreateApprovalWorkflow,
  useDeleteApprovalStep,
  useDeleteApprovalWorkflow,
  useUpdateApprovalStep,
  useUpdateApprovalWorkflow,
} from '@/products/helpdesk/features/approvals/use-approvals';
import { ApiRequestError } from '@/lib/api';

const NONE = 'none';

type WorkflowForm = {
  name: string;
  isActive: boolean;
  ticketType: string;
  categoryId: string;
  minPriority: string;
  minValueEuros: string;
};

type StepForm = {
  name: string;
  mode: ApprovalMode;
  minApprovals: string;
  roleId: string;
  approverIds: string[];
};

function emptyWorkflowForm(): WorkflowForm {
  return {
    name: '',
    isActive: true,
    ticketType: NONE,
    categoryId: NONE,
    minPriority: NONE,
    minValueEuros: '',
  };
}

function fromWorkflow(row: ApprovalWorkflow): WorkflowForm {
  return {
    name: row.name,
    isActive: row.isActive,
    ticketType: row.ticketType ?? NONE,
    categoryId: row.categoryId ?? NONE,
    minPriority: row.minPriority ?? NONE,
    minValueEuros: centsToEuroInput(row.minValueCents),
  };
}

function emptyStepForm(): StepForm {
  return {
    name: '',
    mode: ApprovalMode.SEQUENTIAL,
    minApprovals: '1',
    roleId: NONE,
    approverIds: [],
  };
}

function fromStep(step: ApprovalStep): StepForm {
  return {
    name: step.name,
    mode: step.mode,
    minApprovals: String(step.minApprovals),
    roleId: step.roleId ?? NONE,
    approverIds: step.approvers.map((item) => item.id),
  };
}

function toWorkflowBody(
  form: WorkflowForm,
  includeSteps?: UpsertApprovalStepRequest[],
): UpsertApprovalWorkflowRequest {
  return {
    name: form.name.trim(),
    isActive: form.isActive,
    ticketType:
      form.ticketType === NONE
        ? null
        : (form.ticketType as UpsertApprovalWorkflowRequest['ticketType']),
    categoryId: form.categoryId === NONE ? null : form.categoryId,
    minPriority:
      form.minPriority === NONE
        ? null
        : (form.minPriority as UpsertApprovalWorkflowRequest['minPriority']),
    minValueCents: eurosToCents(form.minValueEuros) ?? null,
    steps: includeSteps,
  };
}

function toStepBody(form: StepForm): UpsertApprovalStepRequest {
  return {
    name: form.name.trim(),
    mode: form.mode,
    minApprovals: Number(form.minApprovals) || 1,
    roleId: form.roleId === NONE ? null : form.roleId,
    approverIds: form.approverIds,
  };
}

function matchLabel(row: ApprovalWorkflow): string {
  const parts: string[] = [];
  if (row.ticketType) parts.push(TICKET_TYPE_LABELS[row.ticketType]);
  if (row.categoryName) parts.push(row.categoryName);
  if (row.minPriority) parts.push(`ab ${TICKET_PRIORITY_LABELS[row.minPriority]}`);
  if (row.minValueCents != null && row.minValueCents > 0) {
    parts.push(
      `ab ${(row.minValueCents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'keine Regel';
}

export function ApprovalsPage() {
  const lookups = useApprovalLookups();
  const workflows = useApprovalWorkflows();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = workflows.data?.items ?? [];
  const creating = selectedId === 'new';
  const activeId = creating ? null : (selectedId ?? items[0]?.id ?? null);
  const selected = activeId ? (items.find((item) => item.id === activeId) ?? null) : null;

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Freigabe-Workflows</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Legen Sie fest, welche Ticketart oder Kategorie eine mehrstufige Freigabe auslöst. Bis zur
          Entscheidung bleibt das Ticket pausiert; Genehmiger erhalten eine E-Mail mit
          Freigeben/Ablehnen.
        </p>
      </div>

      {(lookups.isPending || workflows.isPending) && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Workflows werden geladen …
        </p>
      )}

      {workflows.data && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <Card>
            <CardHeader>
              <CardTitle>Regeln</CardTitle>
              <CardDescription>
                Die spezifischste passende Regel gewinnt (Kategorie vor Art vor Schwellenwert).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button type="button" size="sm" onClick={() => setSelectedId('new')}>
                Neuer Workflow
              </Button>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Gilt für</TableHead>
                    <TableHead>Stufen</TableHead>
                    <TableHead>Aktiv</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      className={item.id === activeId ? 'bg-muted/60' : 'cursor-pointer'}
                      onClick={() => setSelectedId(item.id)}
                    >
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{matchLabel(item)}</TableCell>
                      <TableCell>{item.steps.length}</TableCell>
                      <TableCell>{item.isActive ? 'ja' : 'nein'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <WorkflowEditor
            key={creating ? 'new' : (selected?.id ?? 'none')}
            lookups={lookups.data}
            creating={creating}
            workflow={selected}
            onCreated={(id) => setSelectedId(id)}
          />
        </div>
      )}
    </section>
  );
}

function WorkflowEditor({
  lookups,
  creating,
  workflow,
  onCreated,
}: {
  lookups: ReturnType<typeof useApprovalLookups>['data'];
  creating: boolean;
  workflow: ApprovalWorkflow | null;
  onCreated: (id: string) => void;
}) {
  const copy = useVoiceCopy();
  const createWorkflow = useCreateApprovalWorkflow();
  const updateWorkflow = useUpdateApprovalWorkflow();
  const deleteWorkflow = useDeleteApprovalWorkflow();
  const createStep = useCreateApprovalStep();
  const updateStep = useUpdateApprovalStep();
  const deleteStep = useDeleteApprovalStep();
  const [form, setForm] = useState<WorkflowForm>(
    creating ? emptyWorkflowForm() : workflow ? fromWorkflow(workflow) : emptyWorkflowForm(),
  );
  const [stepForm, setStepForm] = useState<StepForm>(emptyStepForm);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [draftSteps, setDraftSteps] = useState<UpsertApprovalStepRequest[]>([]);

  function patch(partial: Partial<WorkflowForm>) {
    setForm((current) => ({ ...current, ...partial }));
  }

  async function onSaveWorkflow(event: FormEvent) {
    event.preventDefault();
    try {
      if (creating) {
        if (draftSteps.length === 0) {
          toast.error(copy('helpdesk.approvals.step-required'));
          return;
        }
        const created = await createWorkflow.mutateAsync(toWorkflowBody(form, draftSteps));
        setDraftSteps([]);
        setForm(emptyWorkflowForm());
        onCreated(created.id);
        toast.success('Workflow angelegt.');
        return;
      }
      if (!workflow) {
        return;
      }
      await updateWorkflow.mutateAsync({ id: workflow.id, body: toWorkflowBody(form) });
      toast.success('Workflow gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function onSaveStep(event: FormEvent) {
    event.preventDefault();
    const body = toStepBody(stepForm);
    if (creating) {
      setDraftSteps((current) =>
        editingStepId && editingStepId !== 'new' && Number.isInteger(Number(editingStepId))
          ? current.map((item, index) => (String(index) === editingStepId ? body : item))
          : [...current, body],
      );
      setEditingStepId(null);
      setStepForm(emptyStepForm());
      return;
    }
    if (!workflow) {
      return;
    }
    try {
      if (editingStepId && editingStepId !== 'new') {
        await updateStep.mutateAsync({ id: editingStepId, body });
      } else {
        await createStep.mutateAsync({ workflowId: workflow.id, body });
      }
      setEditingStepId(null);
      setStepForm(emptyStepForm());
      toast.success('Stufe gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Stufe konnte nicht gespeichert werden.',
      );
    }
  }

  const steps: { id: string; name: string; mode: ApprovalMode; summary: string }[] = creating
    ? draftSteps.map((step, index) => ({
        id: String(index),
        name: step.name,
        mode: step.mode,
        summary: `${APPROVAL_MODE_LABELS[step.mode]} · ${step.approverIds.length} Personen`,
      }))
    : (workflow?.steps ?? []).map((step) => ({
        id: step.id,
        name: step.name,
        mode: step.mode,
        summary: `${APPROVAL_MODE_LABELS[step.mode]} · min. ${step.minApprovals}`,
      }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{creating ? 'Neuer Workflow' : (workflow?.name ?? 'Workflow')}</CardTitle>
          <CardDescription>
            Mindestens eine Regel (Art, Kategorie, Priorität oder Betrag) ist erforderlich.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={(event) => void onSaveWorkflow(event)}>
            <div className="space-y-1.5">
              <Label htmlFor="wf-name">Name</Label>
              <Input
                id="wf-name"
                value={form.name}
                onChange={(event) => patch({ name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ticketart</Label>
              <Select
                value={form.ticketType}
                onValueChange={(value) => patch({ ticketType: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>beliebig</SelectItem>
                  {Object.values(TicketType).map((code) => (
                    <SelectItem key={code} value={code}>
                      {TICKET_TYPE_LABELS[code]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Kategorie</Label>
              <Select
                value={form.categoryId}
                onValueChange={(value) => patch({ categoryId: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>beliebig</SelectItem>
                  {(lookups?.categories ?? []).map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Mindestpriorität</Label>
              <Select
                value={form.minPriority}
                onValueChange={(value) => patch({ minPriority: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>keine</SelectItem>
                  {Object.values(TicketPriority).map((code) => (
                    <SelectItem key={code} value={code}>
                      ab {TICKET_PRIORITY_LABELS[code]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wf-value">Schwellenwert (€, optional)</Label>
              <Input
                id="wf-value"
                inputMode="decimal"
                placeholder="z. B. 500,00"
                value={form.minValueEuros}
                onChange={(event) => patch({ minValueEuros: event.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.isActive}
                onCheckedChange={(value) => patch({ isActive: value === true })}
              />
              Aktiv
            </label>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={createWorkflow.isPending || updateWorkflow.isPending}>
                Speichern
              </Button>
              {!creating && workflow && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="outline">
                      Löschen
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Workflow löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Wenn bereits Freigaben existieren, wird der Workflow nur deaktiviert.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() =>
                          void deleteWorkflow.mutateAsync(workflow.id).then(
                            () => toast.success('Workflow entfernt.'),
                            (error: unknown) =>
                              toast.error(
                                error instanceof ApiRequestError
                                  ? error.message
                                  : 'Löschen fehlgeschlagen.',
                              ),
                          )
                        }
                      >
                        Bestätigen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stufen</CardTitle>
          <CardDescription>
            Mehrere Stufen laufen nacheinander. Innerhalb einer Stufe sequenziell oder parallel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="space-y-2 text-sm">
            {steps.map((step) => (
              <li
                key={step.id}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
              >
                <span>
                  <span className="font-medium">{step.name}</span>
                  <span className="text-muted-foreground"> · {step.summary}</span>
                </span>
                <span className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingStepId(step.id);
                      if (creating) {
                        const draft = draftSteps[Number(step.id)];
                        setStepForm(
                          draft
                            ? {
                                ...emptyStepForm(),
                                ...draft,
                                minApprovals: String(draft.minApprovals),
                                roleId: draft.roleId ?? NONE,
                              }
                            : emptyStepForm(),
                        );
                      } else {
                        const full = workflow?.steps.find((item) => item.id === step.id);
                        setStepForm(full ? fromStep(full) : emptyStepForm());
                      }
                    }}
                  >
                    Bearbeiten
                  </Button>
                  {!creating && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void deleteStep.mutateAsync(step.id).then(
                          () => toast.success('Stufe gelöscht.'),
                          (error: unknown) =>
                            toast.error(
                              error instanceof ApiRequestError
                                ? error.message
                                : 'Löschen fehlgeschlagen.',
                            ),
                        )
                      }
                    >
                      Entfernen
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setEditingStepId('new');
              setStepForm(emptyStepForm());
            }}
          >
            Stufe hinzufügen
          </Button>

          {editingStepId && (
            <form className="space-y-3 border-t pt-3" onSubmit={(event) => void onSaveStep(event)}>
              <div className="space-y-1.5">
                <Label htmlFor="step-name">Stufenname</Label>
                <Input
                  id="step-name"
                  value={stepForm.name}
                  onChange={(event) =>
                    setStepForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Modus</Label>
                <Select
                  value={stepForm.mode}
                  onValueChange={(value) =>
                    setStepForm((current) => ({ ...current, mode: value as ApprovalMode }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ApprovalMode).map((code) => (
                      <SelectItem key={code} value={code}>
                        {APPROVAL_MODE_LABELS[code]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="step-min">Mindestanzahl Freigaben</Label>
                <Input
                  id="step-min"
                  type="number"
                  min={1}
                  value={stepForm.minApprovals}
                  onChange={(event) =>
                    setStepForm((current) => ({ ...current, minApprovals: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Rolle (optional)</Label>
                <Select
                  value={stepForm.roleId}
                  onValueChange={(value) =>
                    setStepForm((current) => ({ ...current, roleId: value }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>keine Rolle</SelectItem>
                    {(lookups?.roles ?? []).map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Namentliche Genehmiger</legend>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
                  {(lookups?.users ?? []).map((user) => {
                    const checked = stepForm.approverIds.includes(user.id);
                    return (
                      <label key={user.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) =>
                            setStepForm((current) => ({
                              ...current,
                              approverIds:
                                value === true
                                  ? [...current.approverIds, user.id]
                                  : current.approverIds.filter((id) => id !== user.id),
                            }))
                          }
                        />
                        {user.displayName}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <div className="flex gap-2">
                <Button type="submit">Stufe übernehmen</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingStepId(null);
                    setStepForm(emptyStepForm());
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
